import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNotas } from "../../contexts/NotasContext";
import { nt, COLORES, COLOR_BLANCO, COLOR_PROPIO_BASE, hexValido, paletaNota, splitNumbers, contadorDe, numeroEnPosicion, fetchCounters, errorNota, esConflicto, myId, descargarBlob, intentoDe, etiquetaUltimoResultado, analizarListaPegada, limpiarListaPegada, listarRecordatorios } from "../../lib/notas";
import LogosNumero from "../LogosNumero";
import ResultadoRapido from "./ResultadoRapido";
import NombreCliente from "./NombreCliente";
import VolverALlamar from "./VolverALlamar";

export const AUTOSAVE_MS = 800;
export const LIVE_COUNTERS_DEBOUNCE_MS = 600;
export const LIVE_COUNTERS_POLL_MS = 60000;

/** Un número en reposo solo muestra el número, los logos de cuenta y el contador ×N (decisión 2026-11-03: con 15 números la nota no
 * debe verse como un tablero). El nombre del cliente, el «último resultado» y la campanita «Volver a llamar» solo aparecen con el
 * mouse encima, el foco por teclado o un toque (tacto); se ocultan de nuevo al salir (salvo que el foco se mueva a otro hijo del mismo
 * número). Los botones de RESULTADO en un toque no dependen del hover: aparecen solo si hay un intento propio reciente (attemptId). */
function NumeroEnLinea({ p, c, ult, onNumero, noteId, conAcciones, onResultado, pendienteRecordatorio, onRecordatorioCreado }) {
  const [activo, setActivo] = useState(false);
  const wrapRef = useRef(null);
  const activar = () => setActivo(true);
  const desactivar = (e) => {
    if (wrapRef.current && e?.relatedTarget instanceof Node && wrapRef.current.contains(e.relatedTarget)) return;
    setActivo(false);
  };
  return (
    <span className={`nt-num-wrap${activo ? " activo" : ""}`} ref={wrapRef} data-testid="nt-num-wrap"
      onMouseEnter={activar} onMouseLeave={desactivar} onFocus={activar} onBlur={desactivar} onTouchStart={activar}>
      <button type="button" className="nt-num" data-testid="nt-numero" data-phone={p.phone}
        onClick={(e) => { e.stopPropagation(); onNumero?.(p.phone); }}>
        {p.v}
        <LogosNumero phone={p.phone} />
        {c && c.count > 0 && <span className="nt-c" data-testid="nt-contador" title={`Llamado ${c.count} ${c.count === 1 ? "vez" : "veces"}${ult ? ` · ${ult}` : ""}`}>{c.count}</span>}
        {activo && ult && <span className="nt-c nt-c-ult" data-testid="nt-ultimo-resultado">{ult}</span>}
      </button>
      {conAcciones && (
        <>
          {activo && <NombreCliente phone={p.phone} />}
          <ResultadoRapido phone={p.phone} attemptId={intentoDe(p.phone)} onResultado={onResultado} />
          {!activo && pendienteRecordatorio && <span className="nt-rec-dot" data-testid="nt-recordatorio-pendiente" title="Tiene un recordatorio pendiente" />}
          {activo && <VolverALlamar phone={p.phone} noteId={noteId} onCreado={onRecordatorioCreado} />}
        </>
      )}
    </span>
  );
}

/** Texto con los números marcados: un clic abre «¿por dónde llamar?», sin tope ni confirmación. El círculo es el contador (cuántas veces se ha llamado).
 * `noteId` y `conAcciones` habilitan (si hay una nota propia) el nombre del cliente, el toque de resultado y «Volver a llamar». */
export function TextoConNumeros({ text, counts, onNumero, onVacio, noteId, conAcciones, onResultado, recordatoriosPendientes, onRecordatorioCreado }) {
  const parts = splitNumbers(text);
  return (
    <>
      {parts.map((p, i) => {
        if (p.t === "text") return <span key={i}>{p.v}</span>;
        const c = contadorDe(counts, p.phone);
        const ult = etiquetaUltimoResultado(c?.last_result);
        return (
          <NumeroEnLinea key={i} p={p} c={c} ult={ult} onNumero={onNumero} noteId={noteId} conAcciones={conAcciones}
            onResultado={onResultado} pendienteRecordatorio={recordatoriosPendientes?.has(p.phone)} onRecordatorioCreado={onRecordatorioCreado} />
        );
      })}
      {parts.length === 0 && <span className="nt-ph">{onVacio}</span>}
    </>
  );
}

/** Mismo reconocimiento que arriba, pero SOLO visual (sin botones): es la capa de abajo del editor en vivo. */
export function TextoVivo({ text, counts }) {
  const parts = splitNumbers(text);
  return parts.map((p, i) => {
    if (p.t === "text") return <span key={i}>{p.v}</span>;
    const c = contadorDe(counts, p.phone);
    return (
      <span key={i} className="nt-num nt-num-vivo" data-testid="nt-numero-vivo" data-phone={p.phone}>
        {p.v}
        {c && c.count > 0 && <span className="nt-c" data-testid="nt-contador-vivo">{c.count}</span>}
      </span>
    );
  });
}

/**
 * Editor en una sola capa: espejo con los números reconocidos (subrayados, con ×N y tachado) DEBAJO de un textarea transparente
 * del mismo tipo/alto/relleno/scroll -- el texto se ve a través del espejo y el cursor (caret) es del textarea. Así no hace falta
 * salir del campo para que un número recién escrito/pegado/borrado se reconozca: se ve al instante mientras se sigue escribiendo.
 * Ctrl/Cmd+clic sobre un número abre «¿por dónde llamar?» sin perder el foco (un clic normal solo mueve el cursor, como siempre).
 */
export function NotaTextoVivo({ value, counts, onChange, onNumero, onBlur, placeholder, onListaPegada }) {
  const mirrorRef = useRef(null);
  const sincronizar = (e) => { if (mirrorRef.current) { mirrorRef.current.scrollTop = e.target.scrollTop; mirrorRef.current.scrollLeft = e.target.scrollLeft; } };
  return (
    <div className="nt-live-wrap" data-testid="nt-texto-vivo-wrap">
      <div className="nt-live-mirror nt-live-text" ref={mirrorRef} aria-hidden="true" data-testid="nt-texto-vivo">
        {value ? <TextoVivo text={value} counts={counts} /> : <span className="nt-ph">{placeholder}</span>}
      </div>
      <textarea className="nt-live-area nt-live-text" autoFocus value={value} aria-label="Texto de la nota" data-testid="nt-texto" maxLength={200000}
        onChange={(e) => onChange(e.target.value)} onScroll={sincronizar} onBlur={onBlur} placeholder={placeholder}
        onPaste={(e) => {
          const pegado = e.clipboardData?.getData("text") || "";
          const r = analizarListaPegada(pegado);
          if (r) onListaPegada?.(pegado);
        }}
        onClick={(e) => {
          if (!(e.ctrlKey || e.metaKey)) return;
          const phone = numeroEnPosicion(e.target.value, e.target.selectionStart);
          if (phone) onNumero?.(phone);
        }} />
    </div>
  );
}

/**
 * Nota abierta y editable: título, texto simple y lista de tareas (casillas). Se guarda sola (con versión):
 * un 409 nunca pisa lo del otro -- se avisa y se carga lo más reciente. Sirve igual a pantalla completa, flotante o en la pantalla aparte.
 */
export default function NotaEditor({ noteId, compacto = false, onEstado, onTitulo, onBorrada, onCambio }) {
  const { user } = useAuth();
  const { pedirLlamada, enviarAAparte } = useNotas();
  const [nota, setNota] = useState(null);
  const [draft, setDraft] = useState(null);
  const [estado, setEstado] = useState("cargando");   // cargando | guardado | pendiente | guardando | conflicto | error | no_existe
  const [mensaje, setMensaje] = useState("");
  const [editando, setEditando] = useState(false);
  const [perdido, setPerdido] = useState(null);       // lo que el usuario había escrito cuando hubo conflicto
  const [borrando, setBorrando] = useState(false);
  const [avisoPegado, setAvisoPegado] = useState(null);   // {repetidos, malos} del último pegado, hasta que el usuario decida
  const [liveCounts, setLiveCounts] = useState([]);   // contadores en vivo por número (se piden aparte; no dependen de guardar la nota)
  const [recPendientes, setRecPendientes] = useState(() => new Set());   // teléfonos con recordatorio «Volver a llamar» abierto, para el puntito en reposo
  const notaRef = useRef(null);
  const draftRef = useRef(null);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const timerRef = useRef(null);
  const liveTimerRef = useRef(null);
  const lastPhonesRef = useRef("");
  const vivoRef = useRef(true);
  const onEstadoRef = useRef(onEstado); onEstadoRef.current = onEstado;
  const onTituloRef = useRef(onTitulo); onTituloRef.current = onTitulo;
  const onCambioRef = useRef(onCambio); onCambioRef.current = onCambio;

  const fijarEstado = useCallback((s) => { setEstado(s); onEstadoRef.current?.(s); }, []);
  const aDraft = (n) => ({ title: n.title || "", body: n.body || "", items: (n.items || []).map((x) => ({ ...x })), folder: n.folder || "", color: n.color || "", pinned: !!n.pinned });

  // Teléfonos reconocidos AHORA en el borrador (texto + tareas), aunque todavía no se hayan guardado.
  const numerosDeTexto = useCallback(() => {
    const dr = draftRef.current;
    if (!dr) return [];
    const delCuerpo = splitNumbers(dr.body).filter((p) => p.t === "num").map((p) => p.phone);
    const deTareas = (dr.items || []).flatMap((it) => splitNumbers(it.text).filter((p) => p.t === "num").map((p) => p.phone));
    return Array.from(new Set([...delCuerpo, ...deTareas]));
  }, []);

  // Pide `/attempts/counters` por los números de ESTA nota (en vivo, no depende de guardar). `forzar` ignora si el conjunto no cambió
  // (lo usan el foco y el intervalo de 60s, para que el conteo suba solo tras registrar una llamada; el tecleo solo pide si cambió algo).
  const actualizarContadores = useCallback(async (forzar = false) => {
    if (!vivoRef.current) return;
    const phones = numerosDeTexto();
    const clave = phones.join(",");
    if (!forzar && clave === lastPhonesRef.current) return;
    lastPhonesRef.current = clave;
    if (!phones.length) { if (vivoRef.current) setLiveCounts([]); return; }
    try {
      const items = await fetchCounters(phones);
      if (vivoRef.current) setLiveCounts(items);
    } catch (e) { /* contadores en vivo son de apoyo visual; si falla, se queda con los últimos conocidos (o el valor guardado de la nota) */ }
  }, [numerosDeTexto]);

  // Qué números de ESTA nota tienen un recordatorio «Volver a llamar» todavía abierto (solo para el puntito en reposo; nada
  // grave si llega tarde o falla: la campanita con el detalle real sigue estando disponible al pasar el mouse/foco).
  const cargarRecordatorios = useCallback(async () => {
    try {
      const r = await listarRecordatorios({ note_id: noteId });
      if (vivoRef.current) setRecPendientes(new Set((r.items || []).map((x) => x.phone).filter(Boolean)));
    } catch (e) { /* solo decorativo */ }
  }, [noteId]);

  const cargar = useCallback(async () => {
    try {
      const r = await nt.get(`/${noteId}`);
      if (!vivoRef.current) return false;
      notaRef.current = r.data; setNota(r.data);
      const dr = aDraft(r.data); draftRef.current = dr; setDraft(dr); dirtyRef.current = false;
      onTituloRef.current?.(r.data.title, r.data.color);
      fijarEstado("guardado");
      actualizarContadores(true);   // al abrir la nota: contadores en vivo, sin esperar al debounce
      cargarRecordatorios();
      return true;
    } catch (e) {
      if (!vivoRef.current) return false;
      if (e?.response?.status === 404 || e?.response?.status === 403) { fijarEstado("no_existe"); return false; }
      setMensaje(errorNota(e)); fijarEstado("error");
      return false;
    }
  }, [noteId, fijarEstado, actualizarContadores, cargarRecordatorios]);

  useEffect(() => {
    vivoRef.current = true;
    notaRef.current = null; draftRef.current = null; dirtyRef.current = false; lastPhonesRef.current = "";
    setNota(null); setDraft(null); setPerdido(null); setMensaje(""); setLiveCounts([]); setRecPendientes(new Set()); fijarEstado("cargando");
    cargar();
    return () => { vivoRef.current = false; clearTimeout(timerRef.current); clearTimeout(liveTimerRef.current); };
  }, [noteId, cargar, fijarEstado]);

  // Al teclear/pegar/borrar: pide contadores ~600ms después de dejar de escribir (solo si el conjunto de números cambió).
  useEffect(() => {
    if (!draft) return;
    clearTimeout(liveTimerRef.current);
    liveTimerRef.current = setTimeout(() => actualizarContadores(false), LIVE_COUNTERS_DEBOUNCE_MS);
    return () => clearTimeout(liveTimerRef.current);
  }, [draft?.body, draft?.items, actualizarContadores]);

  // Al recuperar el foco de la ventana, y cada 60s mientras la nota esté visible: para que el conteo suba solo tras registrar una llamada.
  useEffect(() => {
    const alFoco = () => actualizarContadores(true);
    window.addEventListener("focus", alFoco);
    const id = setInterval(() => actualizarContadores(true), LIVE_COUNTERS_POLL_MS);
    return () => { window.removeEventListener("focus", alFoco); clearInterval(id); };
  }, [actualizarContadores]);

  const guardar = useCallback(async () => {
    if (savingRef.current || !dirtyRef.current || !notaRef.current) return;
    savingRef.current = true;
    clearTimeout(timerRef.current);
    const enviado = draftRef.current;
    dirtyRef.current = false;
    if (vivoRef.current) fijarEstado("guardando");
    try {
      const r = await nt.put(`/${noteId}`, { expected_version: notaRef.current.version, title: enviado.title, body: enviado.body, items: enviado.items.map((x) => ({ id: x.id, text: x.text, done: !!x.done })), folder: enviado.folder, color: enviado.color, pinned: enviado.pinned });
      notaRef.current = r.data;
      if (vivoRef.current) {
        setNota(r.data);
        onTituloRef.current?.(r.data.title, r.data.color);
        onCambioRef.current?.(r.data);
        if (dirtyRef.current) { fijarEstado("pendiente"); clearTimeout(timerRef.current); timerRef.current = setTimeout(guardar, AUTOSAVE_MS); } else fijarEstado("guardado");
      }
    } catch (e) {
      if (esConflicto(e)) {
        const actual = e.response.data.detail.current;
        if (vivoRef.current) {
          setPerdido(draftRef.current);
          // Solo se acepta la nota del 409 si viene COMPLETA (texto y lista). Si falta cualquiera, se recarga con GET: jamás se arma un borrador
          // vacío a partir de un resumen (el autoguardado lo habría escrito encima de lo del otro lado).
          const completa = !!actual && typeof actual.body === "string" && Array.isArray(actual.items);
          let ok = true;
          if (completa) { notaRef.current = actual; setNota(actual); const dr = aDraft(actual); draftRef.current = dr; setDraft(dr); }
          else { ok = await cargar(); }
          dirtyRef.current = false;
          if (ok) fijarEstado("conflicto");
        }
      } else {
        dirtyRef.current = true;   // no se perdió: se reintenta con el próximo cambio o al pulsar «Reintentar»
        if (vivoRef.current) { setMensaje(errorNota(e)); fijarEstado("error"); }
      }
    } finally {
      savingRef.current = false;
    }
  }, [noteId, cargar, fijarEstado]);

  // Al cerrar la nota con cambios sin guardar, se intenta guardar antes de irse.
  useEffect(() => () => { if (dirtyRef.current && notaRef.current && !savingRef.current) { guardar(); } }, [guardar]);

  const cambiar = (parche) => {
    const dr = { ...draftRef.current, ...parche };
    draftRef.current = dr; setDraft(dr); dirtyRef.current = true;
    fijarEstado("pendiente");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(guardar, AUTOSAVE_MS);
  };
  const cambiarItem = (id, parche) => cambiar({ items: draftRef.current.items.map((x) => (x.id === id ? { ...x, ...parche } : x)) });
  const onRecordatorioCreado = useCallback((phone) => setRecPendientes((s) => { const n = new Set(s); n.add(phone); return n; }), []);

  const exportar = async () => {
    try { const r = await nt.get(`/${noteId}/export`, { responseType: "blob" }); descargarBlob(r.data, `${(draft?.title || "nota").slice(0, 60)}.txt`, "text/plain"); }
    catch (e) { setMensaje(errorNota(e)); }
  };
  const borrar = async () => {
    try { await nt.delete(`/${noteId}?expected_version=${notaRef.current.version}`); dirtyRef.current = false; onBorrada?.(); }
    catch (e) {
      if (esConflicto(e)) { if (await cargar()) fijarEstado("conflicto"); } else { setMensaje(errorNota(e)); }
      setBorrando(false);
    }
  };

  if (estado === "no_existe") {
    return <div className="nt-vacio" data-testid="nt-no-existe"><b>No encontramos esta nota</b><span>Puede que ya no exista o que no sea tuya.</span></div>;
  }
  if (!draft || !nota) return <div className="nt-vacio" data-testid="nt-cargando"><span>Abriendo la nota…</span></div>;

  const propia = !nota.owner_id || String(nota.owner_id) === myId(user);
  const llamar = (phone) => pedirLlamada(phone, propia ? noteId : null);
  const etiqueta = { guardado: `✓ Guardado · v${nota.version}`, pendiente: "Escribiendo…", guardando: "Guardando…", conflicto: "Cambió en otra ventana", error: "No se pudo guardar" }[estado] || "";
  const pillCls = estado === "guardado" ? "ok" : estado === "conflicto" || estado === "error" ? "bad" : "am";

  const pal = paletaNota(draft.color);   // null = hoja oscura de siempre
  const misma = (a, b) => (hexValido(a) || "") === (hexValido(b) || "");
  const swatches = [[COLOR_BLANCO, "Blanco (como un TXT)"], ["", "Oscuro (por defecto)"], ...COLORES.map((c) => [c, `Color ${c}`])];
  const propio = swatches.every(([c]) => !misma(c, draft.color)) ? hexValido(draft.color) : null;   // un color a gusto que no es de la paleta

  // Contadores por número: lo que ya se sabe de la nota guardada, con los contadores EN VIVO encima donde los haya (el historial es del
  // servidor por número; aunque se borre y vuelva a pegar el número, o pase el tiempo sin guardar, el conteo no se pierde ni se queda viejo).
  const mergedCounts = (() => {
    const base = nota.number_counts || [];
    if (!liveCounts.length) return base;
    const map = new Map(base.map((c) => [c.phone, c]));
    for (const c of liveCounts) map.set(c.phone, c);
    return Array.from(map.values());
  })();

  return (
    <div className={`nt-paper${compacto ? " compacto" : ""}${pal ? " tinted" : ""}`} data-testid="nt-editor" data-color={draft.color || ""}
      style={{ "--c": hexValido(draft.color) || COLORES[0], ...(pal ? { "--pbg": pal.bg, "--ptx": pal.tx, "--pnum": pal.num } : {}) }}>
      <div className="nt-row nt-edhead">
        <input className="nt-title" value={draft.title} placeholder="Título de la nota" maxLength={200} aria-label="Título" data-testid="nt-titulo"
          onChange={(e) => cambiar({ title: e.target.value })} />
        <span className={`nt-pill ${pillCls}`} data-testid="nt-estado" data-estado={estado}>{etiqueta}</span>
      </div>

      {(estado === "conflicto") && (
        <div className="nt-aviso" role="alert" data-testid="nt-conflicto">
          <b>Esta nota cambió en otra ventana.</b> Se cargó la versión más reciente para no pisar lo del otro lado.
          {perdido && (
            <span className="nt-row">
              <button type="button" className="nt-btn sm" data-testid="nt-recuperar" onClick={() => { cambiar({ title: perdido.title, body: perdido.body, items: perdido.items, folder: perdido.folder, color: perdido.color, pinned: perdido.pinned }); setPerdido(null); }}>Recuperar mi texto</button>
              <button type="button" className="nt-btn sm" data-testid="nt-dejar-nueva" onClick={() => { setPerdido(null); fijarEstado("guardado"); }}>Dejar la versión nueva</button>
            </span>
          )}
        </div>
      )}
      {estado === "error" && (
        <div className="nt-aviso" role="alert" data-testid="nt-error">{mensaje || "No se pudo guardar."} Tu texto sigue aquí.{" "}
          <button type="button" className="nt-btn sm" data-testid="nt-reintentar" onClick={() => { dirtyRef.current = true; guardar(); }}>Reintentar</button></div>
      )}

      {avisoPegado && (
        <div className="nt-aviso" role="status" data-testid="nt-aviso-pegado">
          Esa lista pegada tiene {avisoPegado.repetidos ? `${avisoPegado.repetidos} número${avisoPegado.repetidos === 1 ? "" : "s"} repetido${avisoPegado.repetidos === 1 ? "" : "s"}` : ""}
          {avisoPegado.repetidos && avisoPegado.malos ? " y " : ""}
          {avisoPegado.malos ? `${avisoPegado.malos} mal escrito${avisoPegado.malos === 1 ? "" : "s"}` : ""}.
          <span className="nt-row">
            <button type="button" className="nt-btn sm" data-testid="nt-limpiar-lista" onClick={() => { cambiar({ body: limpiarListaPegada(draftRef.current.body) }); setAvisoPegado(null); }}>Limpiar</button>
            <button type="button" className="nt-btn sm" data-testid="nt-limpiar-no" onClick={() => setAvisoPegado(null)}>Dejar así</button>
          </span>
        </div>
      )}
      {editando ? (
        <NotaTextoVivo value={draft.body} counts={mergedCounts} onChange={(v) => cambiar({ body: v })} onNumero={llamar}
          onBlur={() => setEditando(false)} placeholder="Escribe aquí…" onListaPegada={(txt) => setAvisoPegado(analizarListaPegada(txt))} />
      ) : (
        <div className="nt-txt" role="textbox" tabIndex={0} aria-label="Texto de la nota" data-testid="nt-texto-vista"
          onClick={() => setEditando(true)} onKeyDown={(e) => { if (e.key === "Enter") setEditando(true); }}>
          <TextoConNumeros text={draft.body} counts={mergedCounts} onNumero={llamar} onVacio="Toca aquí para escribir…"
            noteId={propia ? noteId : null} conAcciones={propia} onResultado={() => actualizarContadores(true)}
            recordatoriosPendientes={recPendientes} onRecordatorioCreado={onRecordatorioCreado} />
        </div>
      )}

      <div className="nt-tareas" data-testid="nt-tareas">
        {draft.items.map((it) => {
          const nums = splitNumbers(it.text).filter((p) => p.t === "num");
          return (
            <div className={`nt-tarea${it.done ? " hecha" : ""}`} key={it.id} data-testid="nt-tarea">
              <input type="checkbox" checked={!!it.done} aria-label="Hecha" onChange={(e) => cambiarItem(it.id, { done: e.target.checked })} />
              <input className="nt-tarea-txt" value={it.text} placeholder="Tarea" maxLength={500} aria-label="Texto de la tarea" onChange={(e) => cambiarItem(it.id, { text: e.target.value })} />
              {nums.map((p, i) => { const c = contadorDe(mergedCounts, p.phone); return (
                <button type="button" key={i} className="nt-num" data-testid="nt-numero" data-phone={p.phone} onClick={() => llamar(p.phone)}>{p.v}<LogosNumero phone={p.phone} />{c && c.count > 0 && <span className="nt-c" data-testid="nt-contador">{c.count}</span>}</button>); })}
              <button type="button" className="nt-x" aria-label="Quitar tarea" onClick={() => cambiar({ items: draft.items.filter((x) => x.id !== it.id) })}>✕</button>
            </div>
          );
        })}
      </div>

      <div className="nt-row nt-pie">
        <span className="nt-colores" aria-label="Color de la nota">
          {swatches.map(([c, nombre]) => <button type="button" key={c || "oscuro"} className={`nt-sw${misma(c, draft.color) ? " on" : ""}`} style={{ background: c || "#15161c" }} title={nombre} aria-label={nombre} aria-pressed={misma(c, draft.color)} data-testid="nt-color" onClick={() => cambiar({ color: c })} />)}
          <label className={`nt-own${propio ? " on" : ""}`} title="Elegir otro color">
            <input type="color" value={hexValido(draft.color) || COLOR_PROPIO_BASE} aria-label="Mi color" data-testid="nt-color-propio" onChange={(e) => cambiar({ color: e.target.value })} /><span>+ Mi color</span>
          </label>
        </span>
        <input className="nt-folder" value={draft.folder} placeholder="Carpeta" maxLength={60} aria-label="Carpeta" data-testid="nt-carpeta" onChange={(e) => cambiar({ folder: e.target.value })} />
        <span className="nt-sp" />
        <button type="button" className={`nt-btn sm${draft.pinned ? " on" : ""}`} aria-pressed={draft.pinned} data-testid="nt-fijar" onClick={() => cambiar({ pinned: !draft.pinned })}>{draft.pinned ? "★ Fijada" : "☆ Fijar"}</button>
        {!compacto && <button type="button" className="nt-btn sm" data-testid="nt-separar" onClick={() => enviarAAparte(noteId)}>⧉ Pantalla aparte</button>}
        <button type="button" className="nt-btn sm" data-testid="nt-exportar" onClick={exportar}>⇩ Exportar TXT</button>
        {!borrando
          ? <button type="button" className="nt-btn sm" data-testid="nt-borrar" onClick={() => setBorrando(true)}>⌫ Borrar</button>
          : <span className="nt-row" data-testid="nt-borrar-confirmar">¿Mover a la papelera?
            <button type="button" className="nt-btn sm bad" data-testid="nt-borrar-si" onClick={borrar}>Sí, borrar</button>
            <button type="button" className="nt-btn sm" onClick={() => setBorrando(false)}>No</button></span>}
      </div>
      {mensaje && estado !== "error" && <div className="nt-hint" role="status">{mensaje}</div>}
      <div className="nt-hint">{propia ? "Dueño: usted" : "Nota de otra persona de su equipo"} · Se guarda sola mientras escribe</div>
    </div>
  );
}
